import React from "react";
import { FaRegClock } from "react-icons/fa6";
import { FaUser } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { UscisFee } from "../types/application";
import { formatCurrency } from "../help/Utils";

type ButtonType = "primary" | "secondary" | "bluefil";

interface Button {
  label: string;
  type: ButtonType;
  link: string;
}

interface DocumentFeedbackItem {
  id: number;
  fileName: string;
  documentCategory: string;
  status: string;
}

interface VisaApplicationCardProps {
  title: string;
  label: string;
  status: string;
  statusColor: string;
  statusTextColor: string;
  statusBorderColor: string;
  updatedDate: string;
  totalSteps: number;
  completedSteps: number;
  missingDocs: number;
  rightArrow: string;
  buttonStyleSm?: React.CSSProperties;
  buttonloginstyle2Sm?: React.CSSProperties;
  dotColor: string;
  buttons?: Button[];
  uscisFee?: UscisFee | null;
  applicationId?: string;
  visaType?: string;
  /** Person the application is for (dashboard) */
  beneficiaryName?: string;
  hasUnreadFeedback?: boolean;
  /** Pending reviewer notes (dashboard); shows numeric badge when positive */
  feedbackPendingItemCount?: number;
  hasDocumentFeedback?: boolean;
  documentFeedbackPendingCount?: number;
  documentFeedbackItems?: DocumentFeedbackItem[];
}

const VisaApplicationCard: React.FC<VisaApplicationCardProps> = ({
  title,
  label,
  status,
  statusColor,
  statusTextColor,
  updatedDate,
  totalSteps,
  completedSteps,
  missingDocs,
  rightArrow,
  buttonStyleSm,
  buttonloginstyle2Sm,
  dotColor,
  statusBorderColor,
  buttons = [],
  uscisFee,
  applicationId,
  visaType,
  beneficiaryName,
  hasUnreadFeedback = false,
  feedbackPendingItemCount = 0,
  hasDocumentFeedback = false,
  documentFeedbackPendingCount = 0,
  documentFeedbackItems = [],
}) => {
  // An explicitly computed $0 fee (a statutory exemption or an I-912 waiver).
  // A null amount means the fee was never computed, which is not the same thing.
  const hasNoUscisFeeDue =
    uscisFee?.amount != null && Number(uscisFee.amount) === 0;
  const safeTotal = totalSteps > 0 ? totalSteps : 1;
  const progressPercent = Math.min(
    100,
    Math.max(0, (completedSteps / safeTotal) * 100)
  );

  const formCount = Math.max(0, feedbackPendingItemCount);
  const docCount = Math.max(0, documentFeedbackPendingCount);
  const totalActionCount = formCount + docCount;
  const showActionDot =
    totalActionCount === 0 && (hasUnreadFeedback || hasDocumentFeedback);

  const badgeTitleParts: string[] = [];
  if (formCount > 0) {
    badgeTitleParts.push(
      `${formCount} form note${formCount === 1 ? "" : "s"}`
    );
  }
  if (docCount > 0) {
    badgeTitleParts.push(
      `${docCount} document${docCount === 1 ? "" : "s"} need attention`
    );
  }

  const getButtonStyle = (type: ButtonType): React.CSSProperties => {
    if (type === "primary") return buttonStyleSm || {};
    if (type === "secondary") return buttonloginstyle2Sm || {};
    return {};
  };

  const getWidthClass = (): string => {
    if (buttons.length === 1) return "sm:basis-full";
    if (buttons.length === 2) return "sm:basis-1/2";
    if (buttons.length === 3) return "sm:basis-1/3";
    if (buttons.length === 4) return "sm:basis-1/4";
    return "sm:flex-1"; // fallback
  };

  return (
    <div className="visaCard relative flex h-full min-h-0 w-full flex-col border border-gray-200 bg-white p-6 shadow-sm rounded-2xl">
      {totalActionCount > 0 ? (
        <span
          className="absolute -top-1 right-3 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-xs font-semibold flex items-center justify-center ring-2 ring-white shadow-sm"
          title={badgeTitleParts.join(" · ")}
          aria-label={badgeTitleParts.join(", ") || "Action required"}
        >
          {totalActionCount > 99 ? "99+" : totalActionCount}
        </span>
      ) : (
        showActionDot && (
          <span
            className="absolute -top-1 right-3 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white"
            title="Action required"
            aria-label="Action required"
          />
        )
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-4 flex justify-between">
          <div className="min-w-0 pe-2">
            <h5 className="font-bold text-lg ">
              {title}
              {label?.trim() ? (
                <>
                  {" "}
                  <span className="text-xs font-normal px-[7px] py-1 bg-gray-200 rounded-full ms-2 align-middle">
                    {label}
                  </span>
                </>
              ) : null}
            </h5>
            {beneficiaryName ? (
              <p
                className="mt-1 flex items-center gap-1.5 text-sm text-[#6B7280]"
                aria-label={`Beneficiary ${beneficiaryName}`}
              >
                <FaUser className="text-[12px] text-[#6B7280]" aria-hidden />
                <span className="font-medium text-[#1B365D]">{beneficiaryName}</span>
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-start pt-1">
            <p
              className="flex items-center text-xs text-[#4B5563] whitespace-nowrap"
              aria-label={`Updated ${updatedDate}`}
              title={`Updated: ${updatedDate}`}
            >
              <FaRegClock className="mr-1.5 text-[14px] text-[#6B7280]" aria-hidden />
              {updatedDate}
            </p>
          </div>
        </div>
        <div className="lg:flex lg:items-center lg:justify-between">
          <button
            type="button"
            className="mb-3 rounded-3xl px-4 py-2 text-sm font-semibold lg:mb-0"
            style={{
              backgroundColor: statusColor,
              border: "1px solid",
              borderColor: statusBorderColor,
              color: statusTextColor,
            }}
          >
            {status}
          </button>
          <div />
        </div>
        <div className="mx-auto mt-4 w-full max-w-2xl">
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-[#F4A261] transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between">
            <span className="text-base text-[#1B365D]">Progress</span>
            <span className="text-base text-[#1B365D]">
              {totalSteps <= 0 ? "—" : `${Math.round(progressPercent)}%`}
            </span>
          </div>
        </div>

        {docCount > 0 && documentFeedbackItems.length > 0 && (
          <div className="mt-3 rounded-xl border border-red-100 bg-red-50/80 px-3 py-2">
            <p className="text-xs font-semibold text-[#991B1B]">
              Document feedback ({docCount})
            </p>
            <ul className="mt-1 space-y-0.5">
              {documentFeedbackItems.slice(0, 4).map((item) => (
                <li
                  key={item.id}
                  className="truncate text-xs text-[#7F1D1D]"
                  title={item.fileName}
                >
                  {item.fileName}
                  <span className="text-[#B91C1C]/opacity-80">
                    {" "}
                    · {item.status === "needs_reupload" ? "reupload" : "rejected"}
                  </span>
                </li>
              ))}
              {docCount > 4 && (
                <li className="text-xs text-[#B91C1C]">
                  +{docCount - 4} more
                </li>
              )}
            </ul>
          </div>
        )}

        {uscisFee &&
          // A computed $0 fee is 'not_applicable' but still worth showing —
          // "no fee due" is information the applicant wants. Only a fee that
          // was never computed (null amount) is hidden outright.
          (hasNoUscisFeeDue ||
            (uscisFee.status !== "not_applicable" &&
              uscisFee.status !== "skipped")) && (
            <div className="mt-1 rounded bg-blue-50 px-[10px] py-2 text-sm text-[#1B365D]">
              <div className="flex items-center justify-between">
                <span className="font-semibold">USCIS Fee:</span>
                <span className="font-bold">
                  {hasNoUscisFeeDue
                    ? "No fee due"
                    : uscisFee.amount
                      ? formatCurrency(uscisFee.amount)
                      : uscisFee.breakdown
                        ? formatCurrency(uscisFee.breakdown.total)
                        : "Pending"}
                </span>
              </div>
              {uscisFee.formGroup && (
                <div className="text-xs text-gray-600 mt-1">
                  Form Group: {uscisFee.formGroup}
                </div>
              )}
              {!hasNoUscisFeeDue && (
                <div className="text-xs text-gray-500 mt-1 capitalize">
                  Status: {uscisFee.status.replace("_", " ")}
                </div>
              )}
            </div>
          )}
      </div>

      <div className="mt-auto shrink-0 border-t border-transparent pt-4 md:flex md:items-center md:justify-between md:gap-3">
        {buttons.map((btn, idx) => (
          <Link
            key={idx}
            to={btn.link}
            className="mt-3 flex w-full items-center justify-center rounded-[50px] border border-[#1B365D] px-3 py-2 text-sm font-semibold leading-6 text-[#1B365D] hover:bg-[#1B365D] hover:text-white md:mt-0"
          >
            {btn.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default VisaApplicationCard;
